import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { useCart } from '../lib/cart';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import CartDrawer from '../components/CartDrawer';
import RetouchRequestModal from '../components/RetouchRequestModal';
import PhotoAnnotationCanvas from '../components/PhotoAnnotationCanvas';
import InstallPWABanner, { InstallPWAButton } from '../components/InstallPWA';
import FaceGroupsPanel from '../components/FaceGroupsPanel';
import SelectionSubmitForm from '../components/SelectionSubmitForm';
import { 
  Camera, 
  Lock,
  Download,
  Heart,
  MessageCircle,
  X,
  ChevronLeft,
  ChevronRight,
  Play,
  Pause,
  Globe,
  Loader2,
  Send,
  ShoppingCart,
  Check,
  Filter,
  SortAsc,
  Paintbrush,
  Grid,
  Columns,
  Square,
  Film,
  LayoutGrid,
  PenTool,
  Users,
  Volume2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Layout icons mapping
const LAYOUT_ICONS = {
  masonry: LayoutGrid,
  justified: Columns,
  grid: Square,
  slideshow: Play,
  filmstrip: Film
};

// Layout CSS class mapping
const LAYOUT_CLASSES = {
  masonry: 'masonry-grid',
  justified: 'justified-grid',
  grid: 'square-grid',
  slideshow: 'slideshow-grid',
  filmstrip: 'filmstrip-grid'
};

export default function PublicGalleryPage() {
  const { publicId } = useParams();
  const { t, language, setLanguage } = useLanguage();
  const { addItem, itemCount } = useCart();
  
  const [gallery, setGallery] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [slideshow, setSlideshow] = useState(false);
  const [commentForm, setCommentForm] = useState({ name: '', content: '' });
  const [showComments, setShowComments] = useState(false);
  const [selectedForDownload, setSelectedForDownload] = useState([]);
  const [downloading, setDownloading] = useState(false);
  
  // New state for cart, filters, retouch
  const [cartOpen, setCartOpen] = useState(false);
  const [retouchModalOpen, setRetouchModalOpen] = useState(false);
  const [selectedPhotoForRetouch, setSelectedPhotoForRetouch] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [currentLayout, setCurrentLayout] = useState('masonry');
  const [themeConfig, setThemeConfig] = useState(null);
  const [fullscreenSlideshow, setFullscreenSlideshow] = useState(false);
  const [annotationPhoto, setAnnotationPhoto] = useState(null);
  const [faceFilterIds, setFaceFilterIds] = useState(null);
  const [selectedForSelection, setSelectedForSelection] = useState([]);
  const [showFacePanel, setShowFacePanel] = useState(false);

  const accessGallery = async (pwd = null) => {
    try {
      // Check for stored client token (from QR code scan)
      const storedToken = sessionStorage.getItem(`gallery_token_${publicId}`);

      const response = await axios.post(`${API}/public/gallery/${publicId}/access`, {
        password: pwd,
        client_token: storedToken || null
      });
      setGallery(response.data.gallery);
      setBranding(response.data.branding);
      setCurrentLayout(response.data.gallery.layout || 'masonry');
      setThemeConfig(response.data.gallery.theme_config || null);
      fetchPhotos();
    } catch (error) {
      if (error.response?.status === 401) {
        setNeedsPassword(true);
        if (pwd) {
          setPasswordError(t('publicGallery.incorrectPassword'));
        }
      } else if (error.response?.status === 410) {
        toast.error(t('publicGallery.expired'));
      } else {
        toast.error(t('errors.notFound'));
      }
      setLoading(false);
    }
  };

  const fetchPhotos = async () => {
    try {
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        favorites_only: favoritesOnly.toString()
      });
      const response = await axios.get(`${API}/public/gallery/${publicId}/photos/filtered?${params}`);
      setPhotos(response.data.photos);
    } catch (error) {
      // Fallback to basic endpoint if filtered fails
      try {
        const response = await axios.get(`${API}/public/gallery/${publicId}/photos`);
        setPhotos(response.data.photos);
      } catch (e) {
        toast.error(t('errors.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    accessGallery();
  }, [publicId]);

  // Refetch when filters change
  useEffect(() => {
    if (gallery) {
      fetchPhotos();
    }
  }, [sortBy, sortOrder, favoritesOnly]);

  useEffect(() => {
    let interval;
    if (slideshow && lightboxOpen) {
      interval = setInterval(() => {
        setCurrentPhotoIndex(prev => (prev + 1) % photos.length);
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [slideshow, lightboxOpen, photos.length]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    setPasswordError('');
    accessGallery(password);
  };

  const handleAddToCart = (photo) => {
    addItem(photo, 49); // Default price 49 PLN
    toast.success(language === 'pl' ? 'Dodano do koszyka' : 'Added to cart');
  };

  const handleRequestRetouch = (photo) => {
    setSelectedPhotoForRetouch(photo);
    setRetouchModalOpen(true);
  };

  const toggleFavorite = async (photoId) => {
    try {
      const response = await axios.post(`${API}/public/photos/${photoId}/favorite`);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_favorite: response.data.is_favorite } : p
      ));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const addComment = async (photoId) => {
    if (!commentForm.name || !commentForm.content) return;
    try {
      const response = await axios.post(`${API}/public/photos/${photoId}/comment`, commentForm);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, comments: [...(p.comments || []), response.data] } : p
      ));
      setCommentForm({ name: '', content: '' });
      toast.success(t('common.success'));
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const downloadPhoto = async (photo) => {
    try {
      // Use watermark endpoint for individual downloads
      const response = await fetch(`${API}/public/photo/${photo.id}/download?watermark=true`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      saveAs(blob, photo.original_filename);
    } catch (error) {
      toast.error(t('errors.generic'));
    }
  };

  const downloadSelected = async () => {
    if (selectedForDownload.length === 0) return;
    setDownloading(true);
    
    try {
      const zip = new JSZip();
      const photosToDownload = photos.filter(p => selectedForDownload.includes(p.id));
      
      for (const photo of photosToDownload) {
        // Use watermark endpoint
        const response = await fetch(`${API}/public/photo/${photo.id}/download?watermark=true`);
        if (!response.ok) continue;
        const blob = await response.blob();
        zip.file(photo.original_filename, blob);
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${gallery.name}.zip`);
      setSelectedForDownload([]);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setDownloading(false);
    }
  };

  const downloadAll = async () => {
    setDownloading(true);
    try {
      const zip = new JSZip();
      
      for (const photo of photos) {
        // Use watermark endpoint
        const response = await fetch(`${API}/public/photo/${photo.id}/download?watermark=true`);
        if (!response.ok) continue;
        const blob = await response.blob();
        zip.file(photo.original_filename, blob);
      }
      
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${gallery.name}.zip`);
    } catch (error) {
      toast.error(t('errors.generic'));
    } finally {
      setDownloading(false);
    }
  };

  const toggleDownloadSelection = (photoId) => {
    setSelectedForDownload(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const favoritePhotos = photos.filter(p => p.is_favorite);
  
  // Apply face filter if set
  const displayedPhotos = faceFilterIds 
    ? photos.filter(p => faceFilterIds.includes(p.id))
    : photos;
    
  const currentPhoto = displayedPhotos[currentPhotoIndex];

  // Password Screen
  if (needsPassword && !gallery) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold mb-2">{t('publicGallery.passwordRequired')}</h1>
            <p className="text-muted-foreground">{t('publicGallery.enterPassword')}</p>
          </div>
          
          <form onSubmit={handlePasswordSubmit}>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="h-12 mb-4"
              data-testid="gallery-password-input"
            />
            {passwordError && (
              <p className="text-destructive text-sm mb-4">{passwordError}</p>
            )}
            <Button type="submit" className="w-full h-12" data-testid="gallery-password-submit">
              {t('common.confirm')}
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {branding?.logo_url ? (
              <img src={branding.logo_url} alt="Logo" className="h-8" />
            ) : (
              <Camera className="w-6 h-6" />
            )}
            <div>
              <h1 className="font-bold">{gallery?.name}</h1>
              <p className="text-xs text-muted-foreground">{gallery?.client_name}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
            >
              <Globe className="w-4 h-4 mr-1" />
              {language.toUpperCase()}
            </Button>
            
            {gallery?.download_enabled && (
              <>
                {selectedForDownload.length > 0 && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={downloadSelected}
                    disabled={downloading}
                    data-testid="download-selected-btn"
                  >
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
                    {t('publicGallery.downloadSelected')} ({selectedForDownload.length})
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={downloadAll}
                  disabled={downloading}
                  data-testid="download-all-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {t('publicGallery.downloadAll')}
                </Button>
              </>
            )}
            
            {/* Shopping Cart Button */}
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setCartOpen(true)}
              className="relative"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-4 h-4" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Button>
            
            {/* Install PWA Button */}
            <InstallPWAButton language={language} />
          </div>
        </div>
      </header>

      {/* PWA Install Banner (mobile) */}
      <InstallPWABanner language={language} galleryName={gallery?.name} />

      {/* Filter Bar */}
      <div className="fixed top-[72px] left-0 right-0 z-30 bg-card/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-4 flex-wrap">
          {/* Layout Switcher */}
          <div className="flex items-center gap-1 border border-border rounded-sm p-1">
            {Object.entries(LAYOUT_ICONS).map(([layout, Icon]) => (
              <button
                key={layout}
                onClick={() => {
                  if (layout === 'slideshow') {
                    setFullscreenSlideshow(true);
                    setCurrentPhotoIndex(0);
                  } else {
                    setCurrentLayout(layout);
                  }
                }}
                className={`p-2 rounded-sm transition-colors ${
                  currentLayout === layout 
                    ? 'bg-primary text-primary-foreground' 
                    : 'hover:bg-secondary'
                }`}
                title={layout.charAt(0).toUpperCase() + layout.slice(1)}
              >
                <Icon className="w-4 h-4" />
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-2">
            <SortAsc className="w-4 h-4 text-muted-foreground" />
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[140px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="created_at">{language === 'pl' ? 'Data' : 'Date'}</SelectItem>
                <SelectItem value="filename">{language === 'pl' ? 'Nazwa' : 'Name'}</SelectItem>
                <SelectItem value="size">{language === 'pl' ? 'Rozmiar' : 'Size'}</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-[100px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">{language === 'pl' ? 'Malejąco' : 'Desc'}</SelectItem>
                <SelectItem value="asc">{language === 'pl' ? 'Rosnąco' : 'Asc'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <Button
            variant={favoritesOnly ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFavoritesOnly(!favoritesOnly)}
            className="h-8"
          >
            <Heart className={`w-4 h-4 mr-1 ${favoritesOnly ? 'fill-current' : ''}`} />
            {t('publicGallery.favorites')} ({favoritePhotos.length})
          </Button>
          
          {/* Face Groups Button */}
          <Button
            variant={showFacePanel ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFacePanel(!showFacePanel)}
            className="h-8"
          >
            <Users className="w-4 h-4 mr-1" />
            {language === 'pl' ? 'Osoby' : 'People'}
          </Button>
          
          <span className="text-sm text-muted-foreground ml-auto">
            {displayedPhotos.length} {language === 'pl' ? 'zdjęć' : 'photos'}
            {faceFilterIds && ` (${language === 'pl' ? 'filtrowane' : 'filtered'})`}
          </span>
        </div>
      </div>

      {/* Face Groups Panel */}
      {showFacePanel && gallery && (
        <div className="fixed top-36 right-4 z-30 w-80">
          <FaceGroupsPanel 
            galleryId={gallery.id}
            language={language}
            onFilterByGroup={(photoIds) => {
              setFaceFilterIds(photoIds);
              setCurrentPhotoIndex(0);
            }}
          />
        </div>
      )}

      {/* Main Gallery */}
      <main className="pt-36 px-6 pb-12">
        <div className="max-w-7xl mx-auto">
          {displayedPhotos.length > 0 ? (
            <div className={LAYOUT_CLASSES[currentLayout] || 'masonry-grid'}>
              {displayedPhotos.map((photo, idx) => (
                <motion.div
                  key={photo.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3, delay: idx * 0.02 }}
                  className={currentLayout === 'masonry' ? 'masonry-item' : 'photo-item'}
                >
                  <div className={`photo-card rounded-sm overflow-hidden border cursor-pointer ${
                    selectedForDownload.includes(photo.id) ? 'border-primary border-2' : 'border-border'
                  }`}>
                    <div 
                      className="relative group"
                      onClick={() => {
                        setCurrentPhotoIndex(idx);
                        setLightboxOpen(true);
                      }}
                    >
                      <img
                        src={photo.thumbnail_url || photo.url}
                        alt={photo.original_filename}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                      
                      <div className="overlay" />
                      
                      {/* Selection Checkbox */}
                      {gallery?.download_enabled && (
                        <div 
                          className={`absolute top-3 left-3 w-6 h-6 rounded-sm border-2 flex items-center justify-center cursor-pointer transition-all ${
                            selectedForDownload.includes(photo.id) 
                              ? 'bg-primary border-primary' 
                              : 'bg-black/50 border-white/50 opacity-0 group-hover:opacity-100'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleDownloadSelection(photo.id);
                          }}
                        >
                          {selectedForDownload.includes(photo.id) && (
                            <Check className="w-4 h-4 text-primary-foreground" />
                          )}
                        </div>
                      )}

                      {/* Selection for Submit Checkbox */}
                      {gallery?.selection_enabled && (
                        <div
                          className={`absolute top-3 right-3 w-6 h-6 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all ${
                            selectedForSelection.includes(photo.id)
                              ? 'bg-yellow-500 border-yellow-500'
                              : 'bg-black/50 border-white/50 opacity-0 group-hover:opacity-100'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedForSelection(prev =>
                              prev.includes(photo.id)
                                ? prev.filter(id => id !== photo.id)
                                : [...prev, photo.id]
                            );
                          }}
                          title="Wybierz do selekcji"
                        >
                          {selectedForSelection.includes(photo.id) && (
                            <Check className="w-4 h-4 text-black" />
                          )}
                        </div>
                      )}

                      {/* Quick Actions */}
                      <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="flex gap-1">
                          <Button 
                            variant="secondary" 
                            size="icon"
                            className="h-8 w-8 bg-black/70 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(photo.id);
                            }}
                          >
                            <Heart className={`w-4 h-4 ${photo.is_favorite ? 'text-red-500 fill-red-500' : ''}`} />
                          </Button>
                          <Button 
                            variant="secondary" 
                            size="icon"
                            className="h-8 w-8 bg-black/70 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(photo);
                            }}
                            title={t('publicGallery.addToCart')}
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-8 w-8 bg-black/70 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRequestRetouch(photo);
                            }}
                            title={language === 'pl' ? 'Zamów retusz' : 'Request retouch'}
                          >
                            <Paintbrush className="w-4 h-4" />
                          </Button>
                          {photo.audio_url && (
                            <Button
                              variant="secondary"
                              size="icon"
                              className="h-8 w-8 bg-black/70 hover:bg-black"
                              onClick={(e) => {
                                e.stopPropagation();
                                const audio = new Audio(photo.audio_url);
                                audio.play();
                              }}
                              title={language === 'pl' ? 'Odtwórz nagranie' : 'Play audio'}
                            >
                              <Volume2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            variant="secondary" 
                            size="icon"
                            className="h-8 w-8 bg-black/70 hover:bg-black"
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowComments(true);
                              setCurrentPhotoIndex(idx);
                              setLightboxOpen(true);
                            }}
                          >
                            <MessageCircle className="w-4 h-4" />
                            {photo.comments?.length > 0 && (
                              <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                                {photo.comments.length}
                              </span>
                            )}
                          </Button>
                          
                          {gallery?.download_enabled && (
                            <Button 
                              variant="secondary" 
                              size="icon"
                              className="h-8 w-8 bg-black/70 hover:bg-black"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadPhoto(photo);
                              }}
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">{t('gallery.noPhotos')}</p>
            </div>
          )}
        </div>
      </main>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && currentPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
          >
            {/* Close Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
              onClick={() => {
                setLightboxOpen(false);
                setSlideshow(false);
                setShowComments(false);
              }}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Navigation */}
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
              onClick={() => setCurrentPhotoIndex(prev => (prev - 1 + photos.length) % photos.length)}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
              onClick={() => setCurrentPhotoIndex(prev => (prev + 1) % photos.length)}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>

            {/* Image */}
            <motion.img
              key={currentPhoto.id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              src={currentPhoto.url}
              alt={currentPhoto.original_filename}
              className="max-h-[85vh] max-w-[85vw] object-contain"
            />

            {/* Bottom Bar */}
            <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/80 to-transparent">
              <div className="max-w-4xl mx-auto flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10"
                    onClick={() => toggleFavorite(currentPhoto.id)}
                  >
                    <Heart className={`w-5 h-5 mr-2 ${currentPhoto.is_favorite ? 'text-red-500 fill-red-500' : ''}`} />
                    {currentPhoto.is_favorite ? t('publicGallery.removeFromFavorites') : t('publicGallery.addToFavorites')}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10"
                    onClick={() => setShowComments(!showComments)}
                  >
                    <MessageCircle className="w-5 h-5 mr-2" />
                    {t('publicGallery.comments')} ({currentPhoto.comments?.length || 0})
                  </Button>
                </div>
                
                <div className="flex items-center gap-4">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10"
                    onClick={() => setAnnotationPhoto(currentPhoto)}
                    title={language === 'pl' ? 'Dodaj adnotację' : 'Add annotation'}
                  >
                    <PenTool className="w-5 h-5 mr-2" />
                    {language === 'pl' ? 'Rysuj' : 'Draw'}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-white hover:bg-white/10"
                    onClick={() => setSlideshow(!slideshow)}
                  >
                    {slideshow ? <Pause className="w-5 h-5 mr-2" /> : <Play className="w-5 h-5 mr-2" />}
                    {t('publicGallery.slideshow')}
                  </Button>
                  {gallery?.download_enabled && (
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-white hover:bg-white/10"
                      onClick={() => downloadPhoto(currentPhoto)}
                    >
                      <Download className="w-5 h-5 mr-2" />
                      {t('publicGallery.downloadSingle')}
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Photo Counter */}
              <div className="text-center mt-4 text-white/60 text-sm">
                {currentPhotoIndex + 1} / {photos.length}
              </div>
            </div>

            {/* Comments Panel */}
            <AnimatePresence>
              {showComments && (
                <motion.div
                  initial={{ x: 300, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: 300, opacity: 0 }}
                  className="absolute right-0 top-0 bottom-0 w-80 bg-card border-l border-border p-6 overflow-y-auto"
                >
                  <h3 className="text-lg font-semibold mb-4">{t('publicGallery.comments')}</h3>
                  
                  {/* Comment List */}
                  <div className="space-y-4 mb-6">
                    {currentPhoto.comments?.map((comment, idx) => (
                      <div key={idx} className="p-3 bg-secondary rounded-sm">
                        <p className="font-medium text-sm">{comment.author_name}</p>
                        <p className="text-sm text-muted-foreground mt-1">{comment.content}</p>
                      </div>
                    ))}
                    {(!currentPhoto.comments || currentPhoto.comments.length === 0) && (
                      <p className="text-sm text-muted-foreground">{t('common.noResults')}</p>
                    )}
                  </div>
                  
                  {/* Add Comment Form */}
                  <div className="space-y-3">
                    <Input
                      placeholder={t('publicGallery.yourName')}
                      value={commentForm.name}
                      onChange={(e) => setCommentForm(prev => ({ ...prev, name: e.target.value }))}
                      className="h-10"
                    />
                    <Textarea
                      placeholder={t('publicGallery.commentPlaceholder')}
                      value={commentForm.content}
                      onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                      className="resize-none"
                      rows={3}
                    />
                    <Button 
                      className="w-full"
                      onClick={() => addComment(currentPhoto.id)}
                      disabled={!commentForm.name || !commentForm.content}
                    >
                      <Send className="w-4 h-4 mr-2" />
                      {t('publicGallery.addComment')}
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cart Drawer */}
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} />
      
      {/* Retouch Request Modal */}
      <RetouchRequestModal 
        photo={selectedPhotoForRetouch}
        isOpen={retouchModalOpen}
        onClose={() => {
          setRetouchModalOpen(false);
          setSelectedPhotoForRetouch(null);
        }}
      />

      {/* Fullscreen Slideshow Mode */}
      <AnimatePresence>
        {fullscreenSlideshow && photos.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black flex items-center justify-center"
            style={themeConfig ? { backgroundColor: themeConfig.bg } : undefined}
          >
            {/* Close Button */}
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute top-4 right-4 z-50 text-white hover:bg-white/10"
              onClick={() => setFullscreenSlideshow(false)}
            >
              <X className="w-6 h-6" />
            </Button>

            {/* Auto-advance slideshow */}
            <SlideshowAutoAdvance 
              photos={photos}
              currentIndex={currentPhotoIndex}
              setCurrentIndex={setCurrentPhotoIndex}
              isActive={fullscreenSlideshow}
            />

            {/* Navigation */}
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
              onClick={() => setCurrentPhotoIndex(prev => (prev - 1 + photos.length) % photos.length)}
            >
              <ChevronLeft className="w-8 h-8" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 z-50 text-white hover:bg-white/10"
              onClick={() => setCurrentPhotoIndex(prev => (prev + 1) % photos.length)}
            >
              <ChevronRight className="w-8 h-8" />
            </Button>

            {/* Current Image */}
            <AnimatePresence mode="wait">
              <motion.img
                key={photos[currentPhotoIndex]?.id}
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.5 }}
                src={photos[currentPhotoIndex]?.url}
                alt={photos[currentPhotoIndex]?.original_filename}
                className="max-h-[90vh] max-w-[90vw] object-contain"
              />
            </AnimatePresence>

            {/* Progress bar and counter */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="max-w-4xl mx-auto">
                {/* Thumbnail strip */}
                <div className="flex justify-center gap-2 mb-4 overflow-x-auto py-2">
                  {photos.slice(
                    Math.max(0, currentPhotoIndex - 3),
                    Math.min(photos.length, currentPhotoIndex + 4)
                  ).map((photo, idx) => {
                    const realIdx = Math.max(0, currentPhotoIndex - 3) + idx;
                    return (
                      <button
                        key={photo.id}
                        onClick={() => setCurrentPhotoIndex(realIdx)}
                        className={`w-16 h-12 rounded-sm overflow-hidden transition-all ${
                          realIdx === currentPhotoIndex 
                            ? 'ring-2 ring-white scale-110' 
                            : 'opacity-50 hover:opacity-80'
                        }`}
                      >
                        <img 
                          src={photo.thumbnail_url || photo.url} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      </button>
                    );
                  })}
                </div>
                
                {/* Counter */}
                <div className="text-center text-white/80">
                  {currentPhotoIndex + 1} / {photos.length}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Annotation Canvas */}
      {annotationPhoto && (
        <PhotoAnnotationCanvas
          imageUrl={annotationPhoto.url}
          existingAnnotations={annotationPhoto.annotations || []}
          language={language}
          onSave={async (annotatedImageUrl) => {
            try {
              // Save annotation to backend
              await axios.post(`${API}/photos/${annotationPhoto.id}/annotation`, {
                type: 'canvas',
                data: annotatedImageUrl
              });
              toast.success(language === 'pl' ? 'Adnotacja zapisana' : 'Annotation saved');
              setAnnotationPhoto(null);
            } catch (error) {
              toast.error(language === 'pl' ? 'Błąd zapisu adnotacji' : 'Failed to save annotation');
            }
          }}
          onClose={() => setAnnotationPhoto(null)}
        />
      )}

      {/* Selection Submit Form */}
      {gallery?.selection_enabled && (
        <SelectionSubmitForm
          galleryId={gallery?.id}
          publicId={publicId}
          selectedPhotoIds={selectedForSelection}
          onClearSelection={() => setSelectedForSelection([])}
          onSubmitSuccess={() => setSelectedForSelection([])}
        />
      )}
    </div>
  );
}

// Helper component for auto-advancing slideshow
function SlideshowAutoAdvance({ photos, currentIndex, setCurrentIndex, isActive }) {
  useEffect(() => {
    if (!isActive || photos.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % photos.length);
    }, 4000); // 4 seconds per photo
    
    return () => clearInterval(interval);
  }, [isActive, photos.length, setCurrentIndex]);
  
  return null;
}
